/**
 * Responsibility: turn a cell's value into something a column can sort by.
 *
 * **Absent is not zero, and that is the whole point of this file.** A card
 * nobody has priced is unknown, not free. Sorting it as zero puts the least
 * informative rows at the top of a column somebody opened precisely to find
 * the dearest, and it makes "cheapest first" a list of cards with no price.
 *
 * So missing values sort to one end and stay there in both directions -
 * which is what you want from a row that has nothing to say.
 *
 * Money arrives in two shapes across these tables: a plain decimal string,
 * and a quote object carrying the amount with its provenance. Both are
 * accepted here rather than making every caller remember which is which.
 */

/** Sorts below every real figure, so unpriced rows sink. */
const ABSENT = Number.NEGATIVE_INFINITY;

type Quote = { amount: string };

function isQuote(value: unknown): value is Quote {
  return typeof value === 'object' && value !== null && 'amount' in value;
}

/** A money or count figure as a number, with absent and unparseable sorting last. */
export function sortableNumber(
  value: string | number | Quote | null | undefined,
): number {
  if (value === null || value === undefined) return ABSENT;
  const raw = isQuote(value) ? value.amount : value;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : ABSENT;
}

/** Text as a case-insensitive key, with absent sorting as empty. */
export function sortableText(value: string | null | undefined): string {
  return (value ?? '').toLocaleLowerCase();
}

/**
 * A date as a sortable key.
 *
 * The ISO strings these APIs return already sort correctly as text, so this
 * exists to say that out loud rather than to convert anything - and to be
 * the one place to change if a format ever stops being ISO.
 */
export function sortableDate(value: string | null | undefined): string {
  return value ?? '';
}
